import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Facebook, Instagram, MapPin, Phone, Mail } from "lucide-react";
import logo from "@/assets/logo-transparent.webp";

const Footer = () => {
  return (
    <footer className="bg-brown text-cream">
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Column */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <img src={logo} alt="Son's Blue River Camp" className="h-14 w-auto" />
            </div>
            <p className="text-cream/80 text-sm leading-relaxed">
              8 acres of family-friendly river paradise on the San Marcos River. Float, grill, relax — then fall asleep to the sound of the river.
            </p>
            <div className="flex gap-4">
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Visit our Facebook page" className="w-10 h-10 rounded-full bg-cream/10 hover:bg-cream/20 flex items-center justify-center transition-colors">
                <Facebook className="h-5 w-5" />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Visit our Instagram page" className="w-10 h-10 rounded-full bg-cream/10 hover:bg-cream/20 flex items-center justify-center transition-colors">
                <Instagram className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="font-heading text-lg font-semibold">Quick Links</h3>
            <nav className="space-y-2">
              <Link to="/book-now" className="block text-cream/80 hover:text-cream transition-colors">Book Now</Link>
              <Link to="/stay-overnight" className="block text-cream/80 hover:text-cream transition-colors">Stay Overnight</Link>
              <Link to="/glamping" className="block text-cream/80 hover:text-cream transition-colors">Glamping</Link>
              <Link to="/camping" className="block text-cream/80 hover:text-cream transition-colors">Camping</Link>
              <Link to="/day-rentals" className="block text-cream/80 hover:text-cream transition-colors">Day Rentals</Link>
              <Link to="/day-passes" className="block text-cream/80 hover:text-cream transition-colors">Day Passes</Link>
              <Link to="/day-trip" className="block text-cream/80 hover:text-cream transition-colors">River Day Trip</Link>
              <Link to="/cabins" className="block text-cream/80 hover:text-cream transition-colors">Cabins</Link>
              <Link to="/cabana-rentals" className="block text-cream/80 hover:text-cream transition-colors">Cabana Rentals</Link>
              <Link to="/picnic-tables" className="block text-cream/80 hover:text-cream transition-colors">Picnic Tables</Link>
              <Link to="/reviews" className="block text-cream/80 hover:text-cream transition-colors">Reviews</Link>
              <Link to="/about" className="block text-cream/80 hover:text-cream transition-colors">Our Story</Link>
              <Link to="/blog" className="block text-cream/80 hover:text-cream transition-colors">Blog</Link>
              <Link to="/faqs" className="block text-cream/80 hover:text-cream transition-colors">FAQs</Link>
              <Link to="/group-retreats" className="block text-cream/80 hover:text-cream transition-colors">Group Retreats</Link>
              <Link to="/compare" className="block text-cream/80 hover:text-cream transition-colors">Compare Resorts</Link>
              <Link to="/vs-sons-river-ranch" className="block text-cream/80 hover:text-cream transition-colors">vs Son's River Ranch</Link>
              <Link to="/inquire" className="block text-cream/80 hover:text-cream transition-colors">Inquire</Link>
            </nav>
          </div>

          {/* Popular Near Austin */}
          <div className="space-y-4">
            <h3 className="font-heading text-lg font-semibold">Near Austin</h3>
            <nav className="space-y-2">
              <Link to="/log-cabins" className="block text-cream/80 hover:text-cream transition-colors">Log Cabin Rentals</Link>
              <Link to="/river-cabins-austin" className="block text-cream/80 hover:text-cream transition-colors">River Cabins Near Austin</Link>
              <Link to="/tubingaustin" className="block text-cream/80 hover:text-cream transition-colors">Tubing Near Austin</Link>
              <Link to="/kayaksanmarcos" className="block text-cream/80 hover:text-cream transition-colors">Kayaking on the San Marcos</Link>
              <Link to="/blog/cabin-rentals-near-austin" className="block text-cream/80 hover:text-cream transition-colors">Cabin Rentals Guide</Link>
            </nav>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h3 className="font-heading text-lg font-semibold">Contact</h3>
            <div className="space-y-3">
              <a href="tel:+18775777667" className="flex items-center gap-3 text-cream/80 hover:text-cream transition-colors">
                <Phone className="h-5 w-5 text-golden" />
                (877) 577-7667
              </a>
              <a href="mailto:info@sonsgetaways.com" className="flex items-center gap-3 text-cream/80 hover:text-cream transition-colors">
                <Mail className="h-5 w-5 text-golden" />
                info@sonsgetaways.com
              </a>
              <div className="flex items-start gap-3 text-cream/80">
                <MapPin className="h-5 w-5 text-golden flex-shrink-0 mt-0.5" />
                <span>2769 Sherrill Rd<br />Kingsbury, TX 78638</span>
              </div>
            </div>
          </div>

          {/* Newsletter */}
          <div className="space-y-4">
            <h3 className="font-heading text-lg font-semibold">Stay Connected</h3>
            <p className="text-cream/80 text-sm">Get exclusive deals and updates delivered to your inbox.</p>
            <form className="space-y-3" onSubmit={(e) => e.preventDefault()}>
              <Input 
                type="email" 
                placeholder="Enter your email" 
                className="bg-cream/10 border-cream/30 text-cream placeholder:text-cream/80 focus:border-golden"
              />
              <Button variant="warm" className="w-full">Subscribe</Button>
            </form>
            <p className="text-cream/80 text-xs">We respect your inbox. Unsubscribe anytime.</p>
          </div>
        </div>

        {/* City Pages — flatten click depth for SEO */}
        <div className="mt-12 pt-8 border-t border-cream/10 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="space-y-2">
            <h4 className="font-heading text-base font-semibold mb-3">Near Houston</h4>
            <Link to="/camping-houston" className="block text-cream/70 hover:text-cream text-sm">Camping Near Houston</Link>
            <Link to="/tent-camping-houston" className="block text-cream/70 hover:text-cream text-sm">Tent Camping Near Houston</Link>
            <Link to="/river-cabins-houston" className="block text-cream/70 hover:text-cream text-sm">River Cabins Near Houston</Link>
            <Link to="/glamping/houston" className="block text-cream/70 hover:text-cream text-sm">Glamping Near Houston</Link>
            <Link to="/tubinghouston" className="block text-cream/70 hover:text-cream text-sm">Tubing Near Houston</Link>
            <Link to="/kayaking-houston" className="block text-cream/70 hover:text-cream text-sm">Kayaking Near Houston</Link>
            <Link to="/day-trip-houston" className="block text-cream/70 hover:text-cream text-sm">Day Trip From Houston</Link>
          </div>
          <div className="space-y-2">
            <h4 className="font-heading text-base font-semibold mb-3">Near San Antonio</h4>
            <Link to="/camping-san-antonio" className="block text-cream/70 hover:text-cream text-sm">Camping Near San Antonio</Link>
            <Link to="/tent-camping-san-antonio" className="block text-cream/70 hover:text-cream text-sm">Tent Camping Near San Antonio</Link>
            <Link to="/river-cabins-san-antonio" className="block text-cream/70 hover:text-cream text-sm">River Cabins Near San Antonio</Link>
            <Link to="/glamping/san-antonio" className="block text-cream/70 hover:text-cream text-sm">Glamping Near San Antonio</Link>
            <Link to="/tubingsanantonio" className="block text-cream/70 hover:text-cream text-sm">Tubing Near San Antonio</Link>
            <Link to="/kayaking-san-antonio" className="block text-cream/70 hover:text-cream text-sm">Kayaking Near San Antonio</Link>
            <Link to="/day-trip-san-antonio" className="block text-cream/70 hover:text-cream text-sm">Day Trip From San Antonio</Link>
          </div>
          <div className="space-y-2">
            <h4 className="font-heading text-base font-semibold mb-3">Near Austin</h4>
            <Link to="/camping-austin" className="block text-cream/70 hover:text-cream text-sm">Camping Near Austin</Link>
            <Link to="/tent-camping-austin" className="block text-cream/70 hover:text-cream text-sm">Tent Camping Near Austin</Link>
            <Link to="/river-cabins-austin" className="block text-cream/70 hover:text-cream text-sm">River Cabins Near Austin</Link>
            <Link to="/glamping/austin" className="block text-cream/70 hover:text-cream text-sm">Glamping Near Austin</Link>
            <Link to="/tubingaustin" className="block text-cream/70 hover:text-cream text-sm">Tubing Near Austin</Link>
            <Link to="/kayaking-austin" className="block text-cream/70 hover:text-cream text-sm">Kayaking Near Austin</Link>
            <Link to="/day-trip-austin" className="block text-cream/70 hover:text-cream text-sm">Day Trip From Austin</Link>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-cream/10">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-cream/60">
            <p>© 2025 Son's Blue River Camp. All Rights Reserved.</p>
            <div className="flex gap-6">
              <Link to="/waivers" className="hover:text-cream transition-colors">Waivers</Link>
              <Link to="/plan-your-visit" className="hover:text-cream transition-colors">Plan Your Visit</Link>
              <Link to="/locations" className="hover:text-cream transition-colors">Other Locations</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
